import React from "react";
import { FaLock } from "../../utils/icons";
import { FolderDropzone } from "./FolderDropzone";
import { DropzonePanel } from "./DropzonePanel";

/**
 * Entry split for /upload: left = a FOLDER of many studies (batch); right = the
 * existing single-study dropzone (unchanged behavior). Two equal halves.
 */
interface UploadModeChooserProps {
  onSingleFiles: (files: File[]) => void;
  onFolderFiles: (files: File[]) => void;
  maxMb?: number;
  /** Reasons from the last folder that parsed to zero usable studies — surfaced
   *  as a notice so an all-invalid drop isn't silently swallowed. */
  skippedFiles?: { reason: string; count: number }[];
  excludedStudies?: { studyInstanceUid: string; reason: string }[];
}

const ColHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
    {children}
  </div>
);

export const UploadModeChooser: React.FC<UploadModeChooserProps> = ({
  onSingleFiles,
  onFolderFiles,
  maxMb,
  skippedFiles = [],
  excludedStudies = [],
}) => (
  <div data-testid="upload-mode-chooser">
    {(skippedFiles.length > 0 || excludedStudies.length > 0) && (
      <div
        className="mb-4 rounded border border-warn/40 bg-warn/5 p-3 text-[12px] text-fg/70"
        data-testid="chooser-empty-folder-notice"
      >
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-warn">
          No usable studies in that folder
        </div>
        <ul className="space-y-0.5 text-fg/55">
          {skippedFiles.map((s) => (
            <li key={s.reason}>
              {s.count} file{s.count === 1 ? "" : "s"} — {s.reason}
            </li>
          ))}
          {excludedStudies.map((e) => (
            <li key={e.studyInstanceUid}>
              …{e.studyInstanceUid.slice(-12)} — {e.reason}
            </li>
          ))}
        </ul>
      </div>
    )}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex flex-col">
        <ColHeading>Many studies · folder</ColHeading>
        <FolderDropzone onFiles={onFolderFiles} />
      </div>
      <div className="flex flex-col">
        <ColHeading>One study</ColHeading>
        <DropzonePanel onFiles={onSingleFiles} maxMb={maxMb} />
      </div>
    </div>
    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-fg/40">
      <FaLock className="h-3 w-3 text-fg/30" />
      Uploads run the same pipeline as your worklist and stay private to you.
    </div>
  </div>
);
